import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, ChevronLeft, ChevronRight, Check, CheckCircle } from 'lucide-react';

const BookingWidget = ({ isOpen, onClose, language = 'de' }) => {
  const [step, setStep] = useState(1);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [roomDates, setRoomDates] = useState({}); // Available dates and prices
  const [loadingDates, setLoadingDates] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [formData, setFormData] = useState({
    roomId: '',
    roomType: '',
    numAdult: 1,
    numChild: 0,
    rooms: 1,
    checkIn: null,
    checkOut: null,
    title: 'Herr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    company: '',
    specialRequests: '',
    guestNames: [] // Array with individual guest names
  });

  const [currentMonth, setCurrentMonth] = useState(() => {
    // Start calendar at current month or later
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectingCheckIn, setSelectingCheckIn] = useState(true);

  const translations = {
    de: {
      title: 'Zimmer Buchen',
      step1Title: 'Zimmertyp & Gäste wählen',
      step2Title: 'Daten auswählen',
      step3Title: 'Gäste Information',
      roomType: 'Zimmertyp',
      adults: 'Erwachsene',
      children: 'Kinder (bis 6 Jahre)',
      rooms: 'Anzahl Zimmer',
      numberOfRooms: 'Anzahl Zimmer',
      checkIn: 'Anreise',
      checkOut: 'Abreise',
      continue: 'Weiter',
      back: 'Zurück',
      bookNow: 'Jetzt Buchen',
      guestTitle: 'Anrede',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      address: 'Rechnungsadresse',
      city: 'Stadt',
      postcode: 'Postleitzahl',
      company: 'Firma (optional)',
      specialRequests: 'Besondere Wünsche',
      nights: 'Nächte',
      total: 'Gesamt',
      pricePerNight: 'pro Nacht',
      bookingSummary: 'Buchungsübersicht',
      totalGuests: 'Gäste insgesamt',
      guest: 'Gast',
      guests: 'Gäste',
      room: 'Zimmer',
      months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      days: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      note: 'Hinweis: Ihre Buchungsanfrage wird innerhalb von 24 Stunden bearbeitet.',
      summaryText: 'Zusammenfassung',
      totalPrice: 'Gesamtpreis',
      loadingRooms: 'Lade verfügbare Zimmer...',
      loadingPrices: 'Lade Preise...',
      maxPeople: 'Max. Personen:',
      available: 'Verfügbar:',
      bookingSuccess: 'Buchung erfolgreich!',
      bookingError: 'Fehler bei der Buchung',
      confirmationSent: 'Wir haben Ihre Buchungsbestätigung an',
      tryAgain: 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.',
      priceBreakdown: 'Preisaufschlüsselung',
      selectDates: 'Daten auswählen',
      dateNotAvailable: 'Dieser Zeitraum ist nicht verfügbar',
      dateTooFar: 'Der gewählte Zeitraum liegt zu weit in der Zukunft. Bitte wählen Sie einen näheren Termin oder kontaktieren Sie uns direkt.',
      priceNotAvailable: 'Für diesen Zeitraum sind keine Preise verfügbar',
      breakfastIncluded: 'Frühstück inklusive',
      paymentOnSite: 'Zahlung in der Unterkunft',
      roomNotAvailable: 'Keine Zimmer verfügbar',
      selectedDatesUnavailable: 'Die ausgewählten Daten sind nicht verfügbar. Bitte wählen Sie andere Daten.',
      receptionHoursTitle: 'Wichtiger Hinweis: Öffnungszeiten',
      restaurantHours: 'Restaurant: Freitag – Dienstag: 17:00 – 22:00 (Küche nimmt Bestellungen bis 21:00 an)',
      restaurantClosed: 'Mittwoch und Donnerstag: Geschlossen (Ruhetag)',
      hotelHours: 'Hotel Check-in: Freitag – Dienstag: 15:00 – 18:00',
      hotelSpecialHours: 'Mittwoch und Donnerstag: 17:00 – 18:00',
      hotelPhoneNote: '(Für Ankunft außerhalb dieser Zeit, bitte kontaktieren Sie uns telefonisch)',
      guestNamesTitle: 'Namen der Gäste',
      adult: 'Erwachsener',
      child: 'Kind',
      guestName: 'Name des Gastes',
      breakfasts: 'Frühstücke',
      noChildren: 'Keine Kinder',
      childrenFree: 'Kinder kostenfrei bis 6 Jahre',
      cribOnRequest: 'Kinderbett auf Anfrage'
    },
    en: {
      title: 'Book Room',
      step1Title: 'Choose Room Type & Guests',
      step2Title: 'Choose Dates',
      step3Title: 'Guest Information',
      roomType: 'Room Type',
      adults: 'Adults',
      children: 'Children (up to 6 years)',
      rooms: 'Number of Rooms',
      numberOfRooms: 'Number of Rooms',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      continue: 'Continue',
      back: 'Back',
      bookNow: 'Book Now',
      guestTitle: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Billing Address',
      city: 'City',
      postcode: 'Postcode',
      company: 'Company (optional)',
      specialRequests: 'Special Requests',
      nights: 'Nights',
      total: 'Total',
      pricePerNight: 'per night',
      bookingSummary: 'Booking Summary',
      totalGuests: 'Total Guests',
      guest: 'Guest',
      guests: 'Guests',
      room: 'Room',
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      note: 'Note: Your booking request will be processed within 24 hours.',
      summaryText: 'Summary',
      totalPrice: 'Total Price',
      loadingRooms: 'Loading available rooms...',
      loadingPrices: 'Loading prices...',
      maxPeople: 'Max. People:',
      available: 'Available:',
      bookingSuccess: 'Booking successful!',
      bookingError: 'Booking error',
      confirmationSent: 'We have sent your booking confirmation to',
      tryAgain: 'Please try again or contact us directly.',
      priceBreakdown: 'Price Breakdown',
      selectDates: 'Select Dates',
      dateNotAvailable: 'This period is not available',
      dateTooFar: 'The selected period is too far in the future. Please choose a closer date or contact us directly.',
      priceNotAvailable: 'No prices available for this period',
      breakfastIncluded: 'Breakfast included',
      paymentOnSite: 'Payment at the property',
      roomNotAvailable: 'No rooms available',
      selectedDatesUnavailable: 'Selected dates are not available. Please choose different dates.',
      receptionHoursTitle: 'Important Notice: Opening Hours',
      restaurantHours: 'Restaurant: Friday – Tuesday: 5:00 PM – 10:00 PM (Kitchen accepts orders until 9:00 PM)',
      restaurantClosed: 'Wednesday and Thursday: Closed',
      hotelHours: 'Hotel Check-in: Friday – Tuesday: 3:00 PM – 6:00 PM',
      hotelSpecialHours: 'Wednesday and Thursday: 5:00 PM – 6:00 PM',
      hotelPhoneNote: '(For arrival outside these hours, please contact us by phone)',
      guestNamesTitle: 'Guest Names',
      adult: 'Adult',
      child: 'Child',
      guestName: 'Guest Name',
      breakfasts: 'Breakfasts',
      noChildren: 'No Children',
      childrenFree: 'Children free up to 6 years',
      cribOnRequest: 'Crib on request'
    }
  };

  const t = translations[language];

  // API Base URL - Vercel Backend
  const API_BASE_URL = 'https://hotel-dashboard-backend.vercel.app/api';

  // Helper: Get selected room details
  const getSelectedRoom = () => {
    return availableRooms.find(r => r.roomId === formData.roomId);
  };

  // Helper: Calculate total guests
  const getTotalGuests = () => {
    return formData.numAdult + formData.numChild;
  };

  // Helper: Calculate nights
  const calculateNights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;
    return Math.ceil((formData.checkOut - formData.checkIn) / (1000 * 60 * 60 * 24));
  };

  // Helper: Check if dates are available (inventory check)
  const checkDatesAvailable = () => {
    if (!formData.checkIn || !formData.checkOut || Object.keys(roomDates).length === 0) {
      return { available: true, message: '' };
    }

    const nights = calculateNights();
    const checkIn = new Date(formData.checkIn);

    // Check only nights of stay (NOT including checkout day, as checkout is before 12h)
    for (let i = 0; i < nights; i++) {
      const nightDate = new Date(checkIn);
      nightDate.setDate(nightDate.getDate() + i);
      
      // Format date as YYYYMMDD without timezone offset
      const year = nightDate.getFullYear();
      const month = String(nightDate.getMonth() + 1).padStart(2, '0');
      const day = String(nightDate.getDate()).padStart(2, '0');
      const dateKey = `${year}${month}${day}`;
      
      if (roomDates[dateKey]) {
        const inventory = parseInt(roomDates[dateKey].i || 0);
        
        // Check if enough rooms available (i=0 means fully booked)
        if (inventory < formData.rooms) {
          console.error(`❌ Not enough inventory for ${dateKey} (${day}.${month}.${year}): need ${formData.rooms}, available ${inventory}`);
          return { 
            available: false, 
            message: t.selectedDatesUnavailable,
            date: nightDate 
          };
        }
      }
    }

    return { available: true, message: '' };
  };

  // Helper: Calculate total price from roomDates (BOOKING.COM STYLE)
  const calculateTotalPrice = () => {
    if (!formData.checkIn || !formData.checkOut) {
      return 0;
    }

    const nights = calculateNights();
    console.log(`\n💰 CALCULATING PRICE:`);
    console.log(`   Check-in: ${formData.checkIn.toISOString().split('T')[0]}`);
    console.log(`   Check-out: ${formData.checkOut.toISOString().split('T')[0]}`);
    console.log(`   Nights: ${nights}`);
    console.log(`   Rooms: ${formData.rooms}`);
    console.log(`   roomDates keys:`, Object.keys(roomDates));
    console.log(`   roomDates available:`, Object.keys(roomDates).length > 0 ? 'YES' : 'NO');
    
    // Must have roomDates loaded from API
    if (Object.keys(roomDates).length === 0) {
      console.error('❌ Cannot calculate price: roomDates not loaded');
      return 0;
    }
    
    let totalPrice = 0;
    const checkIn = new Date(formData.checkIn);

    // Sum prices for each NIGHT of stay (not including checkout day)
    // NO FALLBACKS - All prices must exist in API data and must be > 0
    for (let i = 0; i < nights; i++) {
      const nightDate = new Date(checkIn);
      nightDate.setDate(nightDate.getDate() + i);
      
      // Format date as YYYYMMDD without timezone offset
      const year = nightDate.getFullYear();
      const month = String(nightDate.getMonth() + 1).padStart(2, '0');
      const day = String(nightDate.getDate()).padStart(2, '0');
      const dateKey = `${year}${month}${day}`;
      
      console.log(`   Night ${i+1}: Looking for date ${dateKey} (${day}.${month}.${year})...`);
      
      if (roomDates[dateKey] && roomDates[dateKey].p1) {
        const nightPrice = parseFloat(roomDates[dateKey].p1);
        
        // Check if price is 0 or negative (not available)
        if (nightPrice <= 0) {
          console.error(`   ❌ Price is €0 for ${dateKey} - Period not available or too far!`);
          return -1; // Special code for unavailable period
        }
        
        totalPrice += nightPrice;
        console.log(`   ✅ Found: €${nightPrice} (running total: €${totalPrice})`);
      } else {
        console.error(`   ❌ MISSING price for ${dateKey} - Cannot calculate total!`);
        return 0;
      }
    }
    
    // All prices found and valid, calculate final
    const finalPrice = totalPrice * formData.rooms;
    console.log(`   Subtotal: €${totalPrice.toFixed(2)}`);
    console.log(`   Final: €${totalPrice.toFixed(2)} × ${formData.rooms} room(s) = €${finalPrice.toFixed(2)}`);
    return finalPrice;
  };

  // Helper: Get price breakdown by night (BOOKING.COM STYLE)
  const getPriceBreakdown = () => {
    if (!formData.checkIn || !formData.checkOut || Object.keys(roomDates).length === 0) {
      return [];
    }

    const nights = calculateNights();
    const nightlyPrices = [];
    const checkIn = new Date(formData.checkIn);

    // Collect price for each night
    for (let i = 0; i < nights; i++) {
      const nightDate = new Date(checkIn);
      nightDate.setDate(nightDate.getDate() + i);
      
      // Format date as YYYYMMDD without timezone offset
      const year = nightDate.getFullYear();
      const month = String(nightDate.getMonth() + 1).padStart(2, '0');
      const day = String(nightDate.getDate()).padStart(2, '0');
      const dateKey = `${year}${month}${day}`;
      
      if (roomDates[dateKey] && roomDates[dateKey].p1) {
        nightlyPrices.push({
          date: new Date(nightDate),
          price: parseFloat(roomDates[dateKey].p1)
        });
      }
    }

    // Group consecutive nights with same price (like Booking.com)
    const grouped = [];
    nightlyPrices.forEach((night, idx) => {
      if (idx === 0 || night.price !== nightlyPrices[idx - 1].price) {
        grouped.push({ count: 1, price: night.price });
      } else {
        grouped[grouped.length - 1].count++;
      }
    });

    return grouped;
  };

  // Fetch available rooms
  useEffect(() => {
    const fetchRooms = async () => {
      if (!isOpen) return;

      try {
        setLoadingRooms(true);
        console.log('Fetching rooms from:', `${API_BASE_URL}/rooms/beds24/all-rooms`);
        
        const response = await fetch(`${API_BASE_URL}/rooms/beds24/all-rooms`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('API Response:', result);
        
        if (result.success && result.data) {
          // Fetch prices for next 30 days for each room to get accurate pricing
          const roomsWithPrices = await Promise.all(
            result.data.map(async (room) => {
              try {
                // Get prices for next 30 days to calculate average/min price
                const today = new Date();
                const futureDate = new Date(today);
                futureDate.setDate(futureDate.getDate() + 30);
                
                const formatDate = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}${month}${day}`;
                };
                
                const from = formatDate(today);
                const to = formatDate(futureDate);
                
                const priceResponse = await fetch(
                  `${API_BASE_URL}/rooms/beds24/get-room-dates?roomId=${room.roomId}&from=${from}&to=${to}`
                );
                
                if (priceResponse.ok) {
                  const priceResult = await priceResponse.json();
                  if (priceResult.success && priceResult.data) {
                    // Find minimum price from available dates
                    const prices = Object.values(priceResult.data)
                      .filter(day => day.p1 && parseFloat(day.p1) > 0)
                      .map(day => parseFloat(day.p1));
                    
                    const minPrice = prices.length > 0 ? Math.min(...prices).toFixed(2) : (room.minPrice || room.price || '0.00');
                    
                    const roomWithPrice = {
                      ...room,
                      minPrice: minPrice
                    };
                    console.log(`Room ${room.roomId} data:`, roomWithPrice);
                    return roomWithPrice;
                  }
                }
                
                // Fallback to existing price
                const roomWithPrice = {
                  ...room,
                  minPrice: room.minPrice || room.price || '0.00'
                };
                console.log(`Room ${room.roomId} data (no price fetch):`, roomWithPrice);
                return roomWithPrice;
              } catch (error) {
                console.error(`Error fetching prices for room ${room.roomId}:`, error);
                return {
                  ...room,
                  minPrice: room.minPrice || room.price || '0.00'
                };
              }
            })
          );
          
          // Sort rooms: single rooms first (maxPeople=1), then double rooms
          const sortedRooms = roomsWithPrices.sort((a, b) => {
            const aPeople = a.maxPeople || 0;
            const bPeople = b.maxPeople || 0;
            return aPeople - bPeople; // Ascending order: 1, 2, 3...
          });
          
          setAvailableRooms(sortedRooms);
          console.log('Rooms loaded with prices (sorted):', sortedRooms);
        } else {
          throw new Error('Invalid API response');
        }
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setAvailableRooms([]);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [isOpen, API_BASE_URL, language]);

  // Fetch room dates and prices for calendar display
  useEffect(() => {
    const fetchRoomDates = async () => {
      if (!formData.roomId) {
        setRoomDates({});
        return;
      }

      try {
        setLoadingDates(true);
        console.log('🔄 Fetching room dates for', formData.rooms, 'room(s)');
        
        const formatDate = (date) => {
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}${month}${day}`;
        };

        // If dates selected, fetch for that range with buffer
        // Otherwise fetch for current calendar month + next month
        let fromDate, toDate;
        
        // IMPORTANT: Beds24 API does NOT allow dates in the past
        // Always ensure fromDate is at least today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (formData.checkIn && formData.checkOut) {
          const checkInDate = new Date(formData.checkIn);
          const checkOutDate = new Date(formData.checkOut);
          
          fromDate = new Date(checkInDate);
          fromDate.setDate(fromDate.getDate() - 1);
          
          // Ensure fromDate is not in the past
          if (fromDate < today) {
            fromDate = today;
          }
          
          toDate = new Date(checkOutDate);
          toDate.setDate(toDate.getDate() + 1);
        } else {
          // Fetch for current calendar month
          const currentMonthStart = new Date(currentMonth);
          const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
          
          fromDate = currentMonthStart;
          
          // Ensure fromDate is not in the past
          if (fromDate < today) {
            fromDate = today;
          }
          
          toDate = new Date(currentMonthEnd);
          toDate.setDate(toDate.getDate() + 30); // Add next month too
        }

        const from = formatDate(fromDate);
        const to = formatDate(toDate);
        
        console.log('🔍 Fetching room dates:', { 
          roomId: formData.roomId, 
          from, 
          to,
          checkInOriginal: formData.checkIn ? formatDate(formData.checkIn) : 'not selected',
          checkOutOriginal: formData.checkOut ? formatDate(formData.checkOut) : 'not selected',
          reason: formData.checkIn && formData.checkOut ? 'dates selected' : 'calendar month'
        });
        console.log('🔍 API URL:', `${API_BASE_URL}/rooms/beds24/get-room-dates?roomId=${formData.roomId}&from=${from}&to=${to}`);
        
        const response = await fetch(
          `${API_BASE_URL}/rooms/beds24/get-room-dates?roomId=${formData.roomId}&from=${from}&to=${to}`
        );

        if (!response.ok) {
          console.error('❌ API response not OK:', response.status, response.statusText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Room dates API response:', result);
        console.log('✅ Room dates data keys count:', result.data ? Object.keys(result.data).length : 0);

        if (result.success && result.data) {
          console.log('\n🏨 ===== BEDS24 API RESPONSE =====');
          console.log('📅 Date range:', from, 'to', to);
          console.log('🛏️ Room ID:', formData.roomId);
          console.log('🔢 Rooms needed:', formData.rooms);
          console.log('📊 Total dates received:', Object.keys(result.data).length);
          console.log('\n📅 Date-by-date breakdown:');
          
          let availableCount = 0;
          let unavailableCount = 0;
          
          Object.entries(result.data).forEach(([dateKey, dateData]) => {
            const inventory = parseInt(dateData.i || 0);
            const price = parseFloat(dateData.p1 || 0);
            const isDateUnavailable = inventory < formData.rooms || price <= 0;
            
            if (isDateUnavailable) {
              unavailableCount++;
              console.log(`   🔴 ${dateKey}: inventory=${inventory}, price=€${price} - UNAVAILABLE (${inventory < formData.rooms ? 'not enough rooms' : 'no price'})`);
            } else {
              availableCount++;
              console.log(`   ✅ ${dateKey}: inventory=${inventory}, price=€${price} - Available`);
            }
          });
          
          console.log(`\n📊 Summary: ${availableCount} available, ${unavailableCount} unavailable`);
          console.log('================================\n');
          
          setRoomDates(result.data);
        } else {
          console.error('❌ Invalid response structure:', result);
          throw new Error('Invalid room dates response');
        }
      } catch (error) {
        console.error('❌ Error fetching room dates:', error);
        setRoomDates({});
      } finally {
        setLoadingDates(false);
      }
    };

    fetchRoomDates();
  }, [formData.roomId, formData.checkIn, formData.checkOut, currentMonth, formData.rooms, API_BASE_URL]);

  // Auto-adjust guest numbers when room or number of rooms changes
  useEffect(() => {
    if (formData.roomId) {
      const selectedRoom = getSelectedRoom();
      if (selectedRoom) {
        const maxAdultsTotal = (selectedRoom.maxAdult || selectedRoom.maxPeople || 2) * formData.rooms;
        const maxChildrenTotal = (selectedRoom.maxChildren || selectedRoom.maxChild || 0) * formData.rooms;
        
        // If current adults exceed max, reduce to max
        if (formData.numAdult > maxAdultsTotal) {
          setFormData(prev => ({
            ...prev,
            numAdult: maxAdultsTotal
          }));
        }
        
        // If current children exceed max, reduce to max
        if (formData.numChild > maxChildrenTotal) {
          setFormData(prev => ({
            ...prev,
            numChild: maxChildrenTotal
          }));
        }
        
        // IMPORTANT: Clear selected dates when changing number of rooms
        // User must re-select dates to ensure availability for new room count
        console.log('🔄 Room count changed to', formData.rooms, '- clearing dates to re-check availability');
      }
    }
  }, [formData.roomId, formData.rooms]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Sync calendar month with check-in date when on step 2
  useEffect(() => {
    if (step === 2 && formData.checkIn) {
      const checkInMonth = new Date(formData.checkIn.getFullYear(), formData.checkIn.getMonth(), 1);
      setCurrentMonth(checkInMonth);
    }
  }, [step, formData.checkIn]);

  if (!isOpen) return null;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const handleDateClick = (date) => {
    console.log('📅 Date clicked:', date);
    console.log('📅 Date string:', date.toISOString());
    console.log('📅 Date key format:', date.toISOString().split('T')[0].replace(/-/g, ''));
    
    // ALLOW ALL DATES: Both check-in and check-out are always allowed
    // Same day can be check-out (guests leave before noon) AND check-in (new guests arrive after noon)
    // Backend will validate final availability
    
    if (selectingCheckIn) {
      console.log('✅ Setting CHECK-IN:', date);
      setFormData({...formData, checkIn: date, checkOut: null});
      setSelectingCheckIn(false);
      // Keep calendar on the same month as check-in
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    } else {
      // Allow booking backwards: if user clicks earlier date, swap check-in/check-out
      if (date > formData.checkIn) {
        console.log('✅ Setting CHECK-OUT:', date);
        setFormData({...formData, checkOut: date});
        setSelectingCheckIn(true);
      } else if (date < formData.checkIn) {
        console.log('✅ Booking backwards: swapping dates');
        console.log('   New CHECK-IN:', date);
        console.log('   New CHECK-OUT:', formData.checkIn);
        setFormData({...formData, checkIn: date, checkOut: formData.checkIn});
        setSelectingCheckIn(true);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setBookingStatus('loading');
      
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const totalPrice = calculateTotalPrice();
      
      const totalGuests = formData.numAdult + formData.numChild;
      
      // Combine special requests with guest names for guestComments
      let guestComments = '';
      if (formData.guestNames && formData.guestNames.length > 0) {
        const guestNamesText = formData.guestNames.filter(name => name && name.trim()).join('\n');
        if (guestNamesText) {
          guestComments += `Guest Names:\n${guestNamesText}`;
        }
      }
      if (formData.specialRequests && formData.specialRequests.trim()) {
        if (guestComments) guestComments += '\n\n';
        guestComments += `Special Requests:\n${formData.specialRequests.trim()}`;
      }
      
      const bookingPayload = {
        guest: {
          name: `${formData.title} ${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone
        },
        roomId: formData.roomId,
        checkIn: formatDate(formData.checkIn),
        checkOut: formatDate(formData.checkOut),
        title: formData.title,
        guestFirstName: formData.firstName,
        guestLastName: formData.lastName,
        guestAddress: formData.address,
        guestCity: formData.city,
        guestPostcode: formData.postcode,
        ...(formData.company && { guestCompany: formData.company }),
        numAdult: formData.numAdult,
        numChild: formData.numChild,
        numRoom: formData.rooms,
        price: parseFloat(totalPrice.toFixed(2)),
        comments: formData.specialRequests || '',
        guestComments: guestComments,
        bookingType: 'web',
        totalGuests: totalGuests,
        totalBreakfasts: totalGuests
      };

      console.log('📤 SENDING BOOKING TO API:');
      console.log('   Room ID:', bookingPayload.roomId);
      console.log('   Check-in:', bookingPayload.checkIn);
      console.log('   Check-out:', bookingPayload.checkOut);
      console.log('   Nights:', calculateNights());
      console.log('   Rooms:', formData.rooms);
      console.log('   Adults:', bookingPayload.numAdult);
      console.log('   Children:', bookingPayload.numChild);
      console.log('   💰 PRICE SENT:', bookingPayload.price);
      console.log('   Full payload:', bookingPayload);

      const response = await fetch(`${API_BASE_URL}/bookings/direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingPayload)
      });

      const result = await response.json();
      console.log('Booking response:', result);
      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);

      if (response.ok && result.success) {
        setBookingStatus('success');
        
        const nights = calculateNights();
        const totalPrice = calculateTotalPrice();
        const selectedRoom = getSelectedRoom();
        
        // Store booking details for success modal
        setBookingDetails({
          roomName: selectedRoom?.name,
          guestName: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          nights: nights,
          adults: formData.numAdult,
          children: formData.numChild,
          rooms: formData.rooms,
          totalPrice: totalPrice
        });
        
        setShowSuccessModal(true);
        
        // Reset form after a delay
        setTimeout(() => {
          // Reset form
          setFormData({
            roomId: '',
            roomType: '',
            numAdult: 1,
            numChild: 0,
            rooms: 1,
            checkIn: null,
            checkOut: null,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            postcode: '',
            company: '',
            specialRequests: '',
            guestNames: []
          });
          setStep(1);
          setBookingStatus(null);
        }, 500);
      } else {
        console.error('Booking failed:', result);
        throw new Error(result.message || `Failed to create booking (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Booking error:', error);
      setBookingStatus('error');
      alert(`${t.bookingError}: ${error.message}\n\n${t.tryAgain}`);
      setBookingStatus(null);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const renderCalendar = () => {
    const days = getDaysInMonth(currentMonth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return days.map((day, index) => {
      if (!day) {
        return <div key={index} className="p-2"></div>;
      }

      const dayDate = new Date(day);
      dayDate.setHours(0, 0, 0, 0);
      
      const isToday = dayDate.toDateString() === today.toDateString();
      const isPast = dayDate < today && !isToday;
      const isCheckIn = formData.checkIn && dayDate.toDateString() === formData.checkIn.toDateString();
      const isCheckOut = formData.checkOut && dayDate.toDateString() === formData.checkOut.toDateString();
      const isInRange = formData.checkIn && formData.checkOut && dayDate > formData.checkIn && dayDate < formData.checkOut;

      // Check if date is available (inventory check)
      // Format date as YYYYMMDD without timezone offset
      const year = dayDate.getFullYear();
      const month = String(dayDate.getMonth() + 1).padStart(2, '0');
      const dayNum = String(dayDate.getDate()).padStart(2, '0');
      const dateKey = `${year}${month}${dayNum}`;
      
      const dateData = roomDates[dateKey];
      const inventory = dateData ? parseInt(dateData.i || 0) : 999; // If no API data for this date, assume available
      const price = dateData ? parseFloat(dateData.p1 || 0) : 999;
      
      // Check availability:
      // CRITICAL RULE: Check-in and Check-out dates IGNORE inventory completely
      // Same day can be check-out (before noon) AND check-in (after noon)
      // Only show red for dates that are NOT check-in/check-out
      
      // Check if date has low inventory
      const hasLowInventory = dateData && (inventory < formData.rooms || price <= 0);
      
      // Show RED for dates with low inventory EXCEPT selected dates (check-in/check-out/in-range)
      // Red dates are still clickable - user can try to book, backend validates
      const isUnavailableVisual = hasLowInventory && !isCheckIn && !isCheckOut && !isInRange;
      
      // Debug log for low inventory dates
      if (hasLowInventory && !isPast && !isCheckIn && !isCheckOut) {
        console.log(`🔴 LOW INVENTORY (but clickable): ${dateKey} (${dayNum}.${month}.${year}) - inventory: ${inventory}, rooms needed: ${formData.rooms}, price: €${price}`);
      }

      return (
        <button
          key={index}
          type="button"
          onClick={() => !isPast && handleDateClick(dayDate)}
          disabled={isPast}
          className={`
            relative p-2 text-sm rounded-lg transition-colors
            ${
              // Priority: Selected dates (gold) > Past (gray) > Low inventory (red but clickable) > Normal
              isPast ? 'text-gray-300 cursor-not-allowed' :
              isCheckIn ? 'bg-luxury-gold text-white font-bold rounded-l-xl cursor-pointer' :
              isCheckOut ? 'bg-luxury-gold text-white font-bold rounded-r-xl cursor-pointer' :
              isInRange ? 'bg-luxury-gold/30 rounded-none cursor-pointer' :
              isUnavailableVisual ? 'bg-red-500 text-white cursor-pointer font-bold' :
              'hover:bg-luxury-gold/20 cursor-pointer'
            }
            ${isToday && !isCheckIn && !isCheckOut && !isUnavailableVisual && !isPast ? 'ring-2 ring-luxury-gold' : ''}
          `}
        >
          <div className="relative">
            {dayDate.getDate()}
            {isCheckIn && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-semibold whitespace-nowrap">
                IN
              </span>
            )}
            {isCheckOut && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-semibold whitespace-nowrap">
                OUT
              </span>
            )}
          </div>
        </button>
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-luxury-charcoal">{t.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          
          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <h3 className="text-xl font-bold text-luxury-charcoal mb-6">{t.step1Title}</h3>
              
              {loadingRooms && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luxury-gold mx-auto mb-4"></div>
                  <p className="text-gray-600">{t.loadingRooms}</p>
                </div>
              )}

              {!loadingRooms && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">{t.roomType}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availableRooms.map((room) => {
                        const isSelected = formData.roomId === room.roomId;
                        return (
                          <button
                            key={room.roomId}
                            type="button"
                            onClick={() => setFormData({
                              ...formData, 
                              roomId: room.roomId,
                              roomType: room.maxPeople === 1 ? 'single' : 'double',
                              numAdult: 1,
                              numChild: 0
                            })}
                            className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                              isSelected
                                ? 'border-luxury-gold bg-luxury-gold/10 shadow-lg' 
                                : 'border-gray-200 hover:border-luxury-gold/50 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <Users className={`${isSelected ? 'text-luxury-gold' : 'text-gray-400'}`} size={32} />
                              {isSelected && (
                                <div className="w-6 h-6 bg-luxury-gold rounded-full flex items-center justify-center">
                                  <Check size={16} className="text-white" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                            <h4 className="font-bold text-xl text-luxury-charcoal">{room.name}</h4>
                            <p className="text-sm text-gray-600 mb-2">
                              {language === 'de' ? 'Für bis zu' : 'For up to'} {room.maxPeople} {language === 'de' ? 'Personen' : 'people'}
                            </p>
                            {/* Child Policy Info - only show for Doppelzimmer */}
                            {room.maxPeople > 1 && (
                              <div className="text-xs text-luxury-gold font-semibold">
                                <p>{t.childrenFree}</p>
                                <p className="text-gray-500">{t.cribOnRequest}</p>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!formData.roomId || loadingRooms}
                    className="w-full bg-luxury-gold hover:bg-luxury-darkGold disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {t.continue}
                  </button>
                </>
              )}
            </div>
          )}

          {/* STEP 2 - Gosti, sobe i datumi */}
          {step === 2 && (
            <div>
              <h3 className="text-xl font-bold text-luxury-charcoal mb-6">{t.step2Title}</h3>
              
              {/* Izbor broja gostiju i soba */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.adults}</label>
                  <select 
                    value={formData.numAdult}
                    onChange={(e) => setFormData({...formData, numAdult: parseInt(e.target.value)})}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-luxury-gold focus:outline-none"
                  >
                    {(() => {
                      const selectedRoom = getSelectedRoom();
                      const maxAdultsPerRoom = selectedRoom?.maxAdult || selectedRoom?.maxPeople || 2;
                      // Max adults = adults per room × number of rooms
                      const maxAdults = maxAdultsPerRoom * formData.rooms;
                      const options = [];
                      for (let i = 1; i <= maxAdults; i++) {
                        options.push(<option key={i} value={i}>{i}</option>);
                      }
                      return options;
                    })()}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.children}</label>
                  <select 
                    value={formData.numChild}
                    onChange={(e) => setFormData({...formData, numChild: parseInt(e.target.value)})}
                    disabled={getSelectedRoom()?.maxPeople === 1}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-luxury-gold focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {(() => {
                      const selectedRoom = getSelectedRoom();
                      // Einzelzimmer (maxPeople === 1): No children allowed
                      // Doppelzimmer: Max children = max per room × number of rooms
                      const isSingleRoom = selectedRoom?.maxPeople === 1;
                      const maxChildrenPerRoom = isSingleRoom ? 0 : (selectedRoom?.maxChildren || selectedRoom?.maxChild || 2);
                      const maxChildren = maxChildrenPerRoom * formData.rooms;
                      const options = [];
                      for (let i = 0; i <= maxChildren; i++) {
                        options.push(<option key={i} value={i}>{i}</option>);
                      }
                      return options;
                    })()}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.numberOfRooms}</label>
                  <select 
                    value={formData.rooms}
                    onChange={(e) => setFormData({...formData, rooms: parseInt(e.target.value)})}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-luxury-gold focus:outline-none"
                  >
                    {(() => {
                      const selectedRoom = getSelectedRoom();
                      const maxRooms = Math.min(selectedRoom?.qty || 1, 5);
                      const options = [];
                      for (let i = 1; i <= maxRooms; i++) {
                        // Always use singular 'Zimmer' or 'Room' regardless of count
                        options.push(<option key={i} value={i}>{i} {t.room}</option>);
                      }
                      return options;
                    })()}
                  </select>
                </div>
              </div>

              {/* Summary nakon izbora gostiju */}
              <div className="bg-luxury-gold/10 border-2 border-luxury-gold rounded-xl p-4 mb-6">
                <h4 className="font-bold text-luxury-charcoal mb-3 flex items-center gap-2">
                  <Check size={20} className="text-luxury-gold" />
                  {t.summaryText}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t.roomType}:</span>
                    <span className="font-semibold text-luxury-charcoal">{getSelectedRoom()?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t.numberOfRooms}:</span>
                    <span className="font-semibold text-luxury-charcoal">{formData.rooms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t.totalGuests}:</span>
                    <span className="font-semibold text-luxury-charcoal">
                      {formData.numAdult} {t.adults} + {formData.numChild} {t.children}
                    </span>
                  </div>
                </div>
              </div>

              <h4 className="text-lg font-bold text-luxury-charcoal mb-4">{t.selectDates || (language === 'de' ? 'Daten auswählen' : 'Select Dates')}</h4>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className={`p-4 rounded-xl border-2 transition-colors ${selectingCheckIn ? 'border-luxury-gold bg-luxury-gold/5' : 'border-gray-200'}`}>
                  <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Calendar size={16} />
                    {t.checkIn}
                  </p>
                  <p className="text-lg font-bold text-luxury-charcoal">
                    {formData.checkIn ? formData.checkIn.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '---'}
                  </p>
                </div>
                <div className={`p-4 rounded-xl border-2 transition-colors ${!selectingCheckIn && formData.checkIn ? 'border-luxury-gold bg-luxury-gold/5' : 'border-gray-200'}`}>
                  <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Calendar size={16} />
                    {t.checkOut}                  </p>
                  <p className="text-lg font-bold text-luxury-charcoal">
                    {formData.checkOut ? formData.checkOut.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '---'}
                  </p>
                </div>
              </div>
              
              {formData.checkIn && formData.checkOut && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                  <strong>{calculateNights()} {calculateNights() === 1 ? (language === 'de' ? 'Nacht' : 'night') : (language === 'de' ? 'Nächte' : 'nights')}</strong>
                  {language === 'de' 
                    ? ` - Sie checken ein am ${formData.checkIn.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })} und checken aus am ${formData.checkOut.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}`
                    : ` - You check in on ${formData.checkIn.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} and check out on ${formData.checkOut.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
                  }
                </div>
              )}

              <div className="bg-luxury-pearl/30 rounded-2xl p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <button type="button" onClick={prevMonth} className="p-2 hover:bg-luxury-gold/10 rounded-lg transition-colors">
                    <ChevronLeft size={24} className="text-luxury-charcoal" />
                  </button>
                  <h4 className="text-lg font-bold text-luxury-charcoal">
                    {t.months[new Date(currentMonth).getMonth()]} {new Date(currentMonth).getFullYear()}
                  </h4>
                  <button type="button" onClick={nextMonth} className="p-2 hover:bg-luxury-gold/10 rounded-lg transition-colors">
                    <ChevronRight size={24} className="text-luxury-charcoal" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                  {t.days.map((day) => (
                    <div key={day} className="text-center text-sm font-semibold text-gray-600 p-2">{day}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1" key={`calendar-${Object.keys(roomDates).length}-${formData.rooms}`}>
                  {renderCalendar()}
                </div>
              </div>

              {/* Dynamic Price Display */}
              {loadingDates && formData.checkIn && formData.checkOut && (
                <div className="bg-luxury-pearl/30 border-2 border-gray-200 rounded-xl p-6 mb-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luxury-gold mx-auto mb-3"></div>
                  <p className="text-gray-600">{t.loadingPrices}</p>
                </div>
              )}

              {!loadingDates && formData.checkIn && formData.checkOut && (() => {
                const totalPrice = calculateTotalPrice();
                const availabilityCheck = checkDatesAvailable();
                
                // Check availability first (inventory check)
                if (!availabilityCheck.available) {
                  return (
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="text-red-600 mt-1">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-red-800 mb-1">{t.roomNotAvailable}</h4>
                          <p className="text-sm text-red-700">{availabilityCheck.message}</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Check if period is unavailable (price = -1) or too far (price = 0)
                if (totalPrice === -1) {
                  return (
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="text-red-600 mt-1">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-red-800 mb-1">{t.dateNotAvailable}</h4>
                          <p className="text-sm text-red-700">{t.dateTooFar}</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (totalPrice === 0) {
                  return (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="text-orange-600 mt-1">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-orange-800 mb-1">{t.priceNotAvailable}</h4>
                          <p className="text-sm text-orange-700">{t.tryAgain}</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Valid price - show normal price box
                return (
                  <div className="bg-luxury-gold/10 border-2 border-luxury-gold rounded-xl p-4 mb-6">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{getSelectedRoom()?.name}</span>
                        <span className="font-semibold">{calculateNights()} {t.nights}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{t.rooms}</span>
                        <span className="font-semibold">{formData.rooms}</span>
                      </div>
                      
                      {/* Price breakdown preview */}
                      {getPriceBreakdown().length > 0 && (
                        <div className="border-t border-luxury-gold/30 pt-2 space-y-1">
                          {getPriceBreakdown().map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm text-gray-600">
                              <span>
                                {item.count} × €{item.price.toFixed(2)}
                              </span>
                              <span>
                                €{(item.count * item.price).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="border-t border-luxury-gold/30 pt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-luxury-charcoal">{t.totalPrice}:</span>
                          <span className="text-2xl font-black text-luxury-gold">
                            €{totalPrice.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Breakfast included notice */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                        <div className="flex items-center gap-2 text-green-800">
                          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-semibold">{t.breakfastIncluded}</span>
                        </div>
                      </div>
                      
                      {/* Payment on site notice */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                        <div className="flex items-center gap-2 text-blue-800">
                          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                          </svg>
                          <span className="text-sm font-semibold">{t.paymentOnSite}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex gap-4">
                <button type="button" onClick={() => setStep(1)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold transition-colors">
                  {t.back}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const price = calculateTotalPrice();
                    if (price > 0 && formData.checkIn && formData.checkOut && !loadingDates) {
                      setStep(3);
                    }
                  }}
                  disabled={!formData.checkIn || !formData.checkOut || loadingDates || calculateTotalPrice() <= 0}
                  className="flex-1 bg-luxury-gold hover:bg-luxury-darkGold disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition-colors shadow-lg"
                >
                  {t.continue}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 - Samo zamijeni cijenu */}
          {step === 3 && (
            <div>
              <h3 className="text-xl font-bold text-luxury-charcoal mb-6">{t.step3Title}</h3>
              
              <div className="space-y-4 mb-6">
                {/* Title, First Name, Last Name */}
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.guestTitle} *</label>
                    <select
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                    >
                      <option value="Herr">Herr</option>
                      <option value="Frau">Frau</option>
                      <option value="Dr.">Dr.</option>
                    </select>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.firstName} *</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder="Max"
                    />
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.lastName} *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.email} *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder="max@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.phone} *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder="+49 123 456789"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.address} *</label>
                  <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                    placeholder={language === 'de' ? 'Straße und Hausnummer' : '123 High Street'}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.city} *</label>
                    <input
                      type="text"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder={language === 'de' ? 'Berlin' : 'London'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.postcode} *</label>
                    <input
                      type="text"
                      required
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                      placeholder={language === 'de' ? '10115' : 'SW1A 1AA'}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.company}</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                    placeholder={language === 'de' ? 'Firmenname (optional)' : 'Company name (optional)'}
                  />
                </div>

                {/* Guest Names Section - Individual Input Fields */}
                <div className="border-t-2 border-gray-200 pt-6 mt-6">
                  <h4 className="text-lg font-bold text-luxury-charcoal mb-4">{t.guestNamesTitle} *</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'de' 
                      ? `Bitte geben Sie die Namen aller ${formData.numAdult + formData.numChild} Gäste ein:`
                      : `Please enter the names of all ${formData.numAdult + formData.numChild} guests:`
                    }
                  </p>
                  <div className="space-y-3">
                    {/* Adults */}
                    {Array.from({ length: formData.numAdult }, (_, index) => (
                      <div key={`adult-${index}`}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {language === 'de' ? `Erwachsener ${index + 1}` : `Adult ${index + 1}`} *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.guestNames[index] || ''}
                          onChange={(e) => {
                            const newGuestNames = [...formData.guestNames];
                            newGuestNames[index] = e.target.value;
                            setFormData({...formData, guestNames: newGuestNames});
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                          placeholder={language === 'de' ? 'Max Mustermann' : 'John Doe'}
                        />
                      </div>
                    ))}
                    {/* Children */}
                    {Array.from({ length: formData.numChild }, (_, index) => (
                      <div key={`child-${index}`}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {language === 'de' ? `Kind ${index + 1}` : `Child ${index + 1}`} *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.guestNames[formData.numAdult + index] || ''}
                          onChange={(e) => {
                            const newGuestNames = [...formData.guestNames];
                            newGuestNames[formData.numAdult + index] = e.target.value;
                            setFormData({...formData, guestNames: newGuestNames});
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors"
                          placeholder={language === 'de' ? 'Anna Mustermann' : 'Jane Doe'}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.specialRequests}</label>
                  <textarea
                    rows="3"
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-luxury-gold outline-none transition-colors resize-none"
                    placeholder={language === 'de' ? 'Besondere Wünsche...' : 'Special requests...'}
                  />
                </div>
              </div>

              {/* Booking Summary */}
              <div className="bg-luxury-pearl/50 rounded-2xl p-5 mb-6 border-2 border-luxury-gold/20">
                <h4 className="font-bold text-luxury-charcoal mb-4 text-lg">{t.bookingSummary}</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.roomType}:</span>
                    <span className="font-semibold">{getSelectedRoom()?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.checkIn}:</span>
                    <span className="font-semibold">{formData.checkIn?.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.checkOut}:</span>
                    <span className="font-semibold">{formData.checkOut?.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.nights}:</span>
                    <span className="font-semibold">{calculateNights()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.rooms}:</span>
                    <span className="font-semibold">{formData.rooms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.adults}:</span>
                    <span className="font-semibold">{formData.numAdult}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.children}:</span>
                    <span className="font-semibold">{formData.numChild}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.totalGuests}:</span>
                    <span className="font-semibold">{getTotalGuests()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.breakfasts}:</span>
                    <span className="font-semibold">{getTotalGuests()}</span>
                  </div>
                  
                  {/* Price Breakdown */}
                  <div className="border-t-2 border-luxury-gold/30 pt-3 mt-3">
                    <h5 className="font-bold text-luxury-charcoal mb-2">{t.priceBreakdown}:</h5>
                    {getPriceBreakdown().map((item, idx) => (
                      <div key={idx} className="flex justify-between text-gray-700 mb-1">
                        <span>
                          {item.count} {item.count === 1 ? (language === 'de' ? 'Nacht' : 'night') : (language === 'de' ? 'Nächte' : 'nights')} × €{item.price.toFixed(2)}
                        </span>
                        <span className="font-semibold">
                          €{(item.count * item.price * formData.rooms).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {formData.rooms > 1 && (
                      <div className="text-xs text-gray-500 mt-1">
                        ({formData.rooms} {formData.rooms === 1 ? t.room : t.rooms})
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t-2 border-luxury-gold/30 pt-3 mt-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-luxury-charcoal text-lg">{t.total}:</span>
                      <span className="font-black text-luxury-gold text-2xl">
                        €{calculateTotalPrice().toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Breakfast included notice */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                      <div className="flex items-center gap-2 text-green-800">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-semibold">{t.breakfastIncluded}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Opening Hours Notice */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-blue-600 mt-1 flex-shrink-0">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-blue-900 mb-3">
                      {t.receptionHoursTitle}
                    </h4>
                    <div className="space-y-3 text-sm text-blue-800">
                      {/* Restaurant */}
                      <div className="bg-white/50 rounded-lg p-3">
                        <p className="font-semibold mb-1">🍽️ {t.restaurantHours}</p>
                        <p className="text-xs text-red-600 font-semibold">{t.restaurantClosed}</p>
                      </div>
                      {/* Hotel */}
                      <div className="bg-white/50 rounded-lg p-3">
                        <p className="font-semibold mb-1">🛏️ {t.hotelHours}</p>
                        <p className="font-semibold mb-1">{t.hotelSpecialHours}</p>
                        <p className="text-xs italic">{t.hotelPhoneNote}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setStep(2)} 
                  disabled={bookingStatus === 'loading'}
                  className="flex-1 border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  {t.back}
                </button>
                <button 
                  type="submit" 
                  disabled={bookingStatus === 'loading' || calculateTotalPrice() <= 0}
                  className="flex-1 bg-luxury-gold hover:bg-luxury-darkGold disabled:bg-gray-400 text-white py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  {bookingStatus === 'loading' ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{language === 'de' ? 'Sende...' : 'Sending...'}</span>
                    </>
                  ) : (
                    t.bookNow
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">{t.note}</p>
            </div>
          )}

        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && bookingDetails && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-[fadeIn_0.3s_ease-in-out]">
            <button 
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-luxury-charcoal mb-2">
                {t.bookingSuccess}
              </h3>
              <p className="text-gray-600">
                {t.confirmationSent} {bookingDetails.email}
              </p>
            </div>

            <div className="bg-luxury-pearl/30 rounded-xl p-5 mb-6">
              <h4 className="font-bold text-luxury-charcoal mb-4 text-lg">{t.bookingSummary}</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.guest}:</span>
                  <span className="font-semibold text-luxury-charcoal">{bookingDetails.guestName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.phone}:</span>
                  <span className="font-semibold text-luxury-charcoal">{bookingDetails.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.roomType}:</span>
                  <span className="font-semibold text-luxury-charcoal">{bookingDetails.roomName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.checkIn}:</span>
                  <span className="font-semibold text-luxury-charcoal">
                    {bookingDetails.checkIn?.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.checkOut}:</span>
                  <span className="font-semibold text-luxury-charcoal">
                    {bookingDetails.checkOut?.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.nights}:</span>
                  <span className="font-semibold text-luxury-charcoal">{bookingDetails.nights}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.rooms}:</span>
                  <span className="font-semibold text-luxury-charcoal">{bookingDetails.rooms}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.guests}:</span>
                  <span className="font-semibold text-luxury-charcoal">
                    {bookingDetails.adults} {t.adults} + {bookingDetails.children} {t.children}
                  </span>
                </div>
                <div className="border-t-2 border-luxury-gold/30 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-luxury-charcoal text-lg">{t.total}:</span>
                    <span className="font-black text-luxury-gold text-2xl">
                      €{bookingDetails.totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
              }}
              className="w-full bg-luxury-gold hover:bg-luxury-darkGold text-white py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl"
            >
              {language === 'de' ? 'Schließen' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingWidget;
